import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useUserRole } from "@/hooks/use-user-role";
import { NavBar } from "@/components/NavBar";
import { CurrentLocationProvider } from "@/hooks/use-current-location";
import { HelpWidget } from "@/components/HelpWidget";
import { BillingAlertBanner } from "@/components/BillingAlertBanner";
import { AccessLockedBanner } from "@/components/AccessLockedBanner";
import { PreTrialWelcomeBanner } from "@/components/PreTrialWelcomeBanner";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const { loading: roleLoading, role } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    try {
      sessionStorage.setItem("auth_redirect_reason", "signin_required");
    } catch {}
    navigate({ to: "/login", replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  // Staff (cashiers) must never see the owner shell — every owner page lives
  // under this layout, so gating here covers current and future pages.
  useEffect(() => {
    if (isLoading || roleLoading || !isAuthenticated) return;
    if (role === "cashier") navigate({ to: "/cashier", replace: true });
  }, [isLoading, roleLoading, isAuthenticated, role, navigate]);

  if (isLoading || (isAuthenticated && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Effect above will navigate to /login; render nothing in the meantime.
    return null;
  }

  if (role === "cashier") {
    // Effect above redirects to /cashier; never render owner chrome for staff.
    return null;
  }

  return (
    <CurrentLocationProvider>
      <BillingAlertBanner />
      <PreTrialWelcomeBanner />
      <AccessLockedBanner />
      <NavBar />
      <Outlet />
      <HelpWidget />
    </CurrentLocationProvider>
  );
}
